import React, { useState, useCallback } from 'react';
import { Bot } from '@/types/Bot';
import { useToast } from '@/hooks/use-toast';

const MAX_COMPARISON_ITEMS = 3;

export const useComparison = () => {
  const [comparisonList, setComparisonList] = useState<Bot[]>([]);
  const { toast } = useToast();

  const addToComparison = useCallback((bot: Bot) => {
    setComparisonList(prev => {
      if (prev.some(item => item.id === bot.id)) {
        toast({
          title: "Already in comparison",
          description: `${bot.name} is already in your comparison list.`,
          variant: "default"
        });
        return prev;
      }

      if (prev.length >= MAX_COMPARISON_ITEMS) {
        toast({
          title: "Comparison limit reached",
          description: `You can only compare up to ${MAX_COMPARISON_ITEMS} bots at once.`,
          variant: "destructive"
        });
        return prev;
      }

      toast({
        title: "Added to comparison",
        description: `${bot.name} has been added to your comparison list.`,
        variant: "default"
      });
      
      return [...prev, bot];
    });
  }, [toast]);

  const removeFromComparison = useCallback((botId: string) => {
    setComparisonList(prev => {
      const bot = prev.find(item => item.id === botId);
      if (bot) {
        toast({
          title: "Removed from comparison",
          description: `${bot.name} has been removed from your comparison list.`,
          variant: "default"
        });
      }
      return prev.filter(item => item.id !== botId);
    });
  }, [toast]);

  const clearComparison = useCallback(() => {
    setComparisonList([]);
    toast({
      title: "Comparison cleared",
      description: "All bots have been removed from your comparison list.",
      variant: "default"
    });
  }, [toast]);

  const isInComparison = useCallback((botId: string) => {
    return comparisonList.some(item => item.id === botId);
  }, [comparisonList]);

  const canAddMore = comparisonList.length < MAX_COMPARISON_ITEMS;

  return {
    comparisonList,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isInComparison,
    canAddMore,
    maxItems: MAX_COMPARISON_ITEMS
  };
};